import React from 'react';
import InGridClue from './InGridClue';

const handleFocus = e => {
  setTimeout(() => {
    e.target.setSelectionRange(e.target.value.length, e.target.value.length);
  }, 0);
};

// The editable grid with its clue border. Clue cells of black/outside squares show InGridClue;
// empty border cells claimed by a neighbouring clue are left transparent so the clue can overflow.
export default function CrosswordGrid({
  size, grid, words, clues, claimedCells, gridCell, highlighted, highlightId, hoverWordId, toggleBlack, updateCell, setActiveCell,
}) {
  const countClaimedEmpty = (dRow, dCol, dc) => {
    const ownerKey = `${dRow},${dCol}`;
    let count = 0;
    let c = dCol + dc;
    while (c >= 0 && c < size.width + 1) {
      if (claimedCells[`${dRow},${c}`] === ownerKey) {
        count++;
        c += dc;
      } else break;
    }
    return count;
  };

  return (
    <div className="crossword-grid" style={{ '--cell': `${gridCell}px` }}>
      {Array.from({ length: size.height + 1 }, (_, dRow) => (
        <div key={dRow} className="grid-row">
          {Array.from({ length: size.width + 1 }, (_, dCol) => {
            const r = dRow - 1;
            const c = dCol - 1;
            const isOutOfBounds = r < 0 || c < 0 || r >= size.height || c >= size.width;
            const cell = !isOutOfBounds ? grid[r]?.[c] : null;
            const isBlack = !isOutOfBounds && cell?.isBlack;
            const isClueable = isOutOfBounds || isBlack;

            // Find clues for this display cell (same logic as Flutter)
            let acrossClue = null, downClue = null;
            if (isClueable) {
              const acrossWord = words.find(w => w.direction === 'across' && w.startRow === r && w.startCol === c + 1);
              const downWord = words.find(w => w.direction === 'down' && w.startRow === r + 1 && w.startCol === c);
              if (acrossWord) acrossClue = { word: acrossWord, clue: clues[acrossWord.id] || '' };
              if (downWord) downClue = { word: downWord, clue: clues[downWord.id] || '' };
            }

            const hasClue = acrossClue || downClue;
            const cellKey = `${dRow},${dCol}`;
            const isClaimed = !!claimedCells[cellKey];

            // A claimed empty border cell is rendered transparent so the
            // owning clue cell's content can visually overflow into it.
            if (isClaimed) {
              return <div key={dCol} className="grid-cell grid-cell-claimed" />;
            }

            if (isClueable && hasClue) {
              const emptyRight = countClaimedEmpty(dRow, dCol, 1);
              const emptyLeft = countClaimedEmpty(dRow, dCol, -1);
              return (
                <div key={dCol} className="grid-cell grid-cell-clue has-clue">
                  <InGridClue
                    acrossClue={acrossClue?.clue}
                    downClue={downClue?.clue}
                    cellSize={gridCell}
                    emptyRight={emptyRight}
                    emptyLeft={emptyLeft}
                  />
                </div>
              );
            }

            if (isClueable) {
              return <div key={dCol} className="grid-cell grid-cell-clue" />;
            }

            return (
              <div
                key={dCol}
                className={`grid-cell ${highlighted.has(`${r}-${c}`) ? (highlightId === hoverWordId ? 'hl-hover' : 'hl-active') : ''}`}
                onContextMenu={e => { e.preventDefault(); toggleBlack(r, c); }}
              >
                {cell && (
                  <input
                    type="text"
                    value={cell.letter}
                    onChange={e => updateCell(r, c, e.target.value.slice(-1))}
                    onFocus={e => { setActiveCell({ row: r, col: c }); handleFocus(e); }}
                    maxLength={1}
                    className="cell-input"
                    data-pos={`${r}-${c}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
